// material ui
import { Grid } from '@material-ui/core';
import React from 'react';

// api
import { ResourceActiveDisabled, ResourceActiveEnabled } from '~/src/dash/api/console-api';
import {
  deleteFreewheelSite,
  getFreewheelSites,
  IFreewheelSite,
  updateFreewheelSite,
} from '~/src/dash/api/freewheel-sites';
// components
import { IListTableColumn, ListTable, ListTableRowData } from '~/src/dash/components/list-table';
import { ModelMediumButton } from '~/src/dash/components/model-medium-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import { Panel, Panels } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteFreewheelSiteEdit,
  RouteFreewheelSiteNew,
} from '~/src/dash/router/routes';
import {
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { publicaColors } from '~/src/dash/utils/colors';
// utils
import { decorate } from '~/src/dash/utils/decorator';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { routeName } from '~/src/dash/utils/routes';
// views
import { RowButton } from '~/src/dash/views/component-view';
import { LoadingView } from '~/src/dash/views/loading-view';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

// NOTE: Use ListTable to render the a resource list. When updating a single field (like active or tier)

interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {}

interface IState {
  // list resource
  freewheelSites: IFreewheelSite[];
  loading: boolean;
  // Snackbar messages
  snackbarMessage: string;
  snackbarError: boolean;
  updating: boolean;
}
export const FreewheelListView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      freewheelSites: [] as IFreewheelSite[],
      loading: true,
      snackbarMessage: '',
      snackbarError: false,
      updating: false,
    };

    componentDidMount() {
      this.getFreewheelSites();
    }

    // get freewheel sites via api call and set state
    getFreewheelSites(): void {
      getFreewheelSites(this.props.session.publisher.ID)
        .then((freewheelSites: IFreewheelSite[]) => {
          this.setState({
            freewheelSites,
            loading: false,
          });
        })
        .catch(() => {
          this.setState({
            freewheelSites: [] as IFreewheelSite[],
            snackbarError: true,
            snackbarMessage: 'Error while getting the list of freewheel sites from the API.',
            loading: false,
          });
        });
    }

    // ----- NAVIGATION FUNCTIONS ----- //

    // navigates to existing network setting form
    viewFreewheelSiteSettingsByID = (id: string): void => {
      navigate(RouteFreewheelSiteEdit, extractRouteParams(this.props), {
        freewheelsite: id,
      });
    };

    // navigates to new freewheel site setting form
    createFreewheelSite = (): void => {
      if (this.state.updating) {
        return;
      }
      navigate(
        RouteFreewheelSiteNew,
        extractRouteParams({
          session: this.props.session,
          user: this.props.user,
        }),
      );
    };

    getItemSettingRoute = (id: string): string => {
      return getRoutePath(RouteFreewheelSiteEdit, extractRouteParams(this.props), {
        freewheelsite: id,
      });
    };

    // ----- COMPONENT HELPER  FUNCTIONS ----- //

    handleCloseSnackbar = (): void => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    handleSetUpdating = (updating: boolean) => {
      this.setState({
        updating,
      });
    };

    // ----- HANDLER HELPER FUNCTIONS FOR LIST TABLE ----- //

    // deletes the site and gives user msg if success/failure, returns a promise
    // for ModelDialogDeleteView which requires a promise to close dialog
    handleDeleteFreewheelSite = (freewheelSiteID: string) => {
      deleteFreewheelSite(freewheelSiteID, this.props.session.publisher.ID)
        .then(() => {
          const freewheelSites = this.state.freewheelSites.filter(
            (freewheelSite: IFreewheelSite) => freewheelSite.ID.toString() !== freewheelSiteID,
          );
          this.setState({
            freewheelSites,
            snackbarMessage: 'Successfully deleted freewheel site',
            snackbarError: false,
          });
        })
        .catch((err) => {
          console.error(err);
          this.setState({
            snackbarMessage: 'Error while deleting freewheel site',
            snackbarError: true,
          });
        });
      // the delete modal expects a promise to be returned
      return new Promise((resolve) => resolve(undefined));
    };

    // handles change of network active/inactive and updates the network

    // Note: when updating the REST API's for other resources, see UpdateNetwork / UpdateNetworkHandler
    // which can either update selected fields OR the entire object/associations
    // This is useful since a full object update requires the full object which at times
    // (example: in the bidder list, the bidder object is not completely populated) the resource
    // is only partial and/or we only want to update a single field.
    handleChangeActive = (itemID: string, checked: boolean): void => {
      if (this.state.updating) {
        return;
      }
      const freewheelSites = [...this.state.freewheelSites];
      let selectedIndex = -1;
      // find the freewheel site to be updated
      for (let i = 0; i < freewheelSites.length; i++) {
        if (freewheelSites[i].ID.toString() === itemID) {
          freewheelSites[i].Active = checked ? ResourceActiveEnabled : ResourceActiveDisabled;
          selectedIndex = i;
          break;
        }
      }
      // create a freewheel site with the fields that need to be updated as well as the ID
      if (selectedIndex >= 0) {
        const freewheelSite = {
          ID: freewheelSites[selectedIndex].ID,
          Active: freewheelSites[selectedIndex].Active,
        } as IFreewheelSite;
        this.setState(
          {
            updating: true,
          },
          () => {
            // this is not full update (false) since we only want to update the active field
            updateFreewheelSite(freewheelSite, this.props.session.publisher.ID, false)
              .then(() => {
                this.setState({
                  freewheelSites,
                  snackbarMessage: 'Successfully saved freewheel site.',
                  snackbarError: false,
                  updating: false,
                });
              })
              .catch(() => {
                this.setState({
                  snackbarMessage: 'Failed to save freewheel site.',
                  snackbarError: true,
                  updating: false,
                });
              });
          },
        );
      }
    };

    // ----- RENDER HELPER FUNCTIONS ----- //

    render() {
      const { freewheelSites, loading, snackbarError, snackbarMessage, updating } = this.state;
      const { classes, user, session, location } = this.props;
      const { pathname } = location;
      if (freewheelSites.length === 0 && loading) {
        return <LoadingView active={true} text='Loading the Freewheel Site List' />;
      }

      if (!hasRoleSuperUser(this.props.user.user)) {
        return <LoadingView active={true} text='This feature is super user only' />;
      }
      const columns: IListTableColumn[] = [
        { field: 'Active', title: 'Active', renderType: 'activeswitch', searchable: false },
        { field: 'Name', title: 'Freewheel Site Name', renderType: 'namelink' },
        { field: 'FreewheelPublisherID', title: 'Freewheel Publisher ID', renderType: 'copy' },
        { field: 'Actions', title: 'Actions', renderType: 'delete', searchable: false },
      ];
      // generate row data, the object keys here need to match the fields above
      const data: ListTableRowData[] = freewheelSites.map((freewheelSite: IFreewheelSite) => {
        return {
          // For active field, use boolean since all lists will likely have an active field and the typings may vary
          Active: freewheelSite.Active === ResourceActiveEnabled,
          Name: freewheelSite.Name,
          ID: freewheelSite.ID.toString(),
          FreewheelPublisherID: freewheelSite.FreewheelPublisherID.toString(),
        };
      });
      return (
        <Panels maxWidth vertical relative minWidthMain>
          <Panel
            largeHorizontalPadding
            verticalPadding
            style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
          >
            <ModelTopBar
              title={routeName(pathname)}
              session={session}
              user={user}
              publisher={session.publisher}
              classes={classes}
              delegateElevate={true}
              currentPath={pathname}
              disableTitlePaddingLeft
            />
          </Panel>
          <Panel scroll largeHorizontalPadding verticalPadding>
            <Grid item style={{ paddingTop: 25, paddingBottom: 40 }}>
              <RowButton
                right={
                  <ModelMediumButton
                    variant='contained'
                    color='primary'
                    style={{ paddingLeft: 15, paddingRight: 15 }}
                    onClick={this.createFreewheelSite}
                  >
                    + New Freewheel Site
                  </ModelMediumButton>
                }
              />
            </Grid>
            <Grid item>
              <ListTable
                title={''}
                columns={columns}
                data={data}
                isLoading={loading}
                isUpdating={updating}
                handleChangeActive={this.handleChangeActive}
                getItemSettingRoute={this.getItemSettingRoute}
                handleDeleteItem={this.handleDeleteFreewheelSite}
                handleSetUpdating={this.handleSetUpdating}
              />
            </Grid>
            <SnackbarView
              open={!!snackbarMessage}
              error={snackbarError}
              onClose={this.handleCloseSnackbar}
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);
